import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchGroupTimetable,
  createPeriod as createPeriodApi,
  updatePeriod as updatePeriodApi,
  deletePeriod as deletePeriodApi,
  swapPeriods as swapPeriodsApi,
} from "../service/timetableService";
import type { TimetableState, CreatePeriodPayload, UpdatePeriodPayload } from "../types";
import i18n from "@/i18n";

export function groupTimetableKey(academicGroupId: number, academicYearId: number) {
  return `${academicGroupId}-${academicYearId}`;
}

export const loadGroupTimetable = createAsyncThunk(
  "timetable/loadGroupTimetable",
  async ({ institutionId, academicGroupId, academicYearId }: { institutionId: number; academicGroupId: number; academicYearId: number }) => {
    const periods = await fetchGroupTimetable(institutionId, academicGroupId, academicYearId);
    return { key: groupTimetableKey(academicGroupId, academicYearId), periods };
  }
);

export const addPeriod = createAsyncThunk(
  "timetable/addPeriod",
  async (
    { institutionId, payload }: { institutionId: number; payload: CreatePeriodPayload },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const saved = await createPeriodApi(institutionId, payload);
      await dispatch(loadGroupTimetable({ institutionId, academicGroupId: payload.academicGroupId, academicYearId: payload.academicYearId }));
      return saved.warnings;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("timetable:timetableSlicetsMsgs.failedToSchedulePeriod"));
    }
  }
);

export const editPeriod = createAsyncThunk(
  "timetable/editPeriod",
  async (
    {
      institutionId,
      periodId,
      academicGroupId,
      academicYearId,
      payload,
    }: { institutionId: number; periodId: number; academicGroupId: number; academicYearId: number; payload: UpdatePeriodPayload },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const saved = await updatePeriodApi(institutionId, periodId, payload);
      await dispatch(loadGroupTimetable({ institutionId, academicGroupId, academicYearId }));
      return saved.warnings;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("timetable:timetableSlicetsMsgs.failedToUpdatePeriod"));
    }
  }
);

export const removePeriod = createAsyncThunk(
  "timetable/removePeriod",
  async (
    {
      institutionId,
      periodId,
      academicGroupId,
      academicYearId,
    }: { institutionId: number; periodId: number; academicGroupId: number; academicYearId: number },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await deletePeriodApi(institutionId, periodId);
      await dispatch(loadGroupTimetable({ institutionId, academicGroupId, academicYearId }));
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("timetable:timetableSlicetsMsgs.failedToDeletePeriod"));
    }
  }
);

export const swapPeriods = createAsyncThunk(
  "timetable/swapPeriods",
  async (
    {
      institutionId,
      periodIdA,
      periodIdB,
      academicGroupId,
      academicYearId,
    }: { institutionId: number; periodIdA: number; periodIdB: number; academicGroupId: number; academicYearId: number },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await swapPeriodsApi(institutionId, periodIdA, periodIdB);
      await dispatch(loadGroupTimetable({ institutionId, academicGroupId, academicYearId }));
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("timetable:timetableSlicetsMsgs.failedToSwapPeriods"));
    }
  }
);

const initialState: TimetableState = {
  byGroup: {},
  loading: false,
  error: null,
};

const timetableSlice = createSlice({
  name: "timetable",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadGroupTimetable.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadGroupTimetable.fulfilled, (state, action) => {
        state.loading = false;
        state.byGroup[action.payload.key] = action.payload.periods;
      })
      .addCase(loadGroupTimetable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("timetable:timetableSlicetsMsgs.failedToLoadTimetable");
      })
      .addCase(addPeriod.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("timetable:timetableSlicetsMsgs.failedToSchedulePeriod");
      })
      .addCase(editPeriod.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("timetable:timetableSlicetsMsgs.failedToUpdatePeriod");
      })
      .addCase(removePeriod.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("timetable:timetableSlicetsMsgs.failedToDeletePeriod");
      })
      .addCase(swapPeriods.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("timetable:timetableSlicetsMsgs.failedToSwapPeriods");
      });
  },
});

export default timetableSlice.reducer;
